import { Injectable } from '@angular/core';

/**
 * Thin adapter that wraps browser `window` access behind an injectable boundary.
 *
 * Keeping all direct `window` / `document` references in one place means that:
 * - Unit tests can replace this service with a mock without fighting JSDOM's
 *   non-configurable properties (e.g. `window.location`).
 * - SSR or non-browser environments can provide an alternative implementation
 *   via the Angular DI system rather than scattering `typeof window !== 'undefined'`
 *   guards throughout the codebase.
 */
@Injectable({ providedIn: 'root' })
export class WindowRef {
  /**
   * Returns the native browser `Window` object.
   * Consumers should not call this in non-browser environments; guard with
   * `isPlatformBrowser()` at the call-site if SSR support is ever added.
   */
  get nativeWindow(): Window & typeof globalThis {
    return window;
  }

  /**
   * Reloads the current page via `window.location.reload()`.
   * Delegates here so callers can be tested without stubbing JSDOM internals.
   */
  reload(): void {
    this.nativeWindow.location.reload();
  }
}
